import useFetchAndTrace, { FetchAndTraceErrorHandler } from "../fetchWithTrace";
import { ShippingAddress } from "../../../reducers/uploadedBoard";
import useSWR from "swr";
import settings from "../../../constants/settings";
import useSWRFetcher, { SWRFetchError } from "./fetcher";
import { useCallback, useEffect, useState } from "react";
import { UserSummary } from "../../../reducers/models/users";
import { useCustomAlert, useCustomConfirm } from "../alerts";
import { Response } from "next/dist/compiled/@edge-runtime/primitives";
import { useRouter } from "next/router";

// TODO: move into hooks

export const useProjectOwner = (
  boardId: string | null,
  errorHandler?: FetchAndTraceErrorHandler,
) => {
  const fetcher = useSWRFetcher<UserSummary>(undefined);

  const { data, error } = useSWR<UserSummary, SWRFetchError>(
    boardId === null ? null : `${settings.api_url}/boards/${boardId}/owner`,
    fetcher,
  );

  useEffect(() => {
    if (error !== undefined && errorHandler !== undefined) {
      errorHandler();
    }
  }, [error, errorHandler]);

  return data || null;
};

export const useUser = (
  userId: string | null,
  errorHandler?: FetchAndTraceErrorHandler,
) => {
  const fetcher = useSWRFetcher<UserSummary>(undefined);

  const { data, error, mutate } = useSWR<UserSummary, SWRFetchError>(
    userId === null ? null : `${settings.api_url}/users/${userId}`,
    fetcher,
  );

  useEffect(() => {
    if (userId === null) {
      void mutate(undefined);
    }
  }, [mutate, userId]);

  useEffect(() => {
    if (error !== undefined && errorHandler !== undefined) {
      errorHandler();
    }
  }, [error, errorHandler]);

  const refreshUser = () => {
    void mutate(undefined);
  };

  return { user: data || null, refreshUser };
};

interface ShippingAddressesResponse {
  shipping_addresses: ShippingAddress[];
}

export const useShippingAddresses = (
  userId: string | null,
  errorHandler?: FetchAndTraceErrorHandler,
) => {
  const fetcher = useSWRFetcher<ShippingAddressesResponse>(undefined);

  const { data, mutate, isValidating, error } = useSWR<
    ShippingAddressesResponse,
    SWRFetchError
  >(
    userId === null ? null : `${settings.api_url}/users/${userId}/addresses`,
    fetcher,
  );

  const fetchShippingAddresses = useCallback(() => {
    void mutate();
  }, [mutate]);

  useEffect(() => {
    if (error !== undefined && errorHandler !== undefined) {
      errorHandler();
    }
  }, [error, errorHandler]);

  return {
    shippingAddresses: data?.shipping_addresses || null,
    fetchShippingAddresses,
    loadingAddresses: isValidating,
  };
};

export function useDeleteUser(userId: string) {
  const fetchAndTrace = useFetchAndTrace();
  const customConfirm = useCustomConfirm();
  const customAlert = useCustomAlert();
  const [deleting, setDeleting] = useState(false);
  const router = useRouter();

  const updateUserHandler = async (response: Response) => {
    setDeleting(false);
    if (response.ok) {
      customAlert("User deleted!", () => void router.push("/internal/users"));
    } else {
      if (response.status == 400) {
        const { detail } = (await response.json()) as { detail: string };
        customAlert(detail);
      } else {
        throw Error("Failed to delete user");
      }
    }
  };

  const errorHandler = () => {
    customAlert("Failed to delete user");
  };

  const actuallyDeleteUser = () => {
    setDeleting(true);
    void fetchAndTrace(
      `${settings.api_url}/users/${userId}`,
      updateUserHandler,
      errorHandler,
      {
        method: "DELETE",
      },
    );
  };

  const deleteUser = () => {
    customConfirm(
      "Are you sure you want to delete this user?",
      actuallyDeleteUser,
    );
  };

  return {
    deleteUser,
    deleting,
  };
}
